/**
 * `<StatusMode>` — the calm terminal screen shown once onboarding is
 * `submitted` (or `building`). Lists what the customer connected and links
 * back into the wizard (adding more sources stays allowed in every state).
 */

import { Button, Card, CardContent } from "@cometchat-ai/ui-dashboard";
import { CheckCircle2 } from "../../components/icons";
import { useTranslation } from "react-i18next";

import { StatusPill } from "../../components/status-pill";
import type { OnboardingOverview } from "../api/onboarding";

export function StatusMode({
  overview,
  onAddMore,
}: {
  overview: OnboardingOverview;
  onAddMore: () => void;
}) {
  const { t } = useTranslation("onboarding");

  const connected = overview.groups
    .flatMap((g) => g.sources)
    .filter((s) => s.status === "connected" || s.status === "connecting");

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-6 py-12 text-center">
      <CheckCircle2
        className="h-12 w-12"
        style={{ color: "var(--color-success)" }}
        aria-hidden="true"
      />
      <div className="space-y-2">
        <h1 className="exp-heading-font text-2xl tracking-tight text-foreground">
          {t("statusMode.title")}
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">{t("statusMode.body")}</p>
      </div>

      <Card className="w-full text-left">
        <CardContent className="space-y-3 p-4">
          <h2 className="exp-heading-font text-sm tracking-tight text-foreground">
            {t("statusMode.connectedTitle")}
          </h2>
          {connected.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("statusMode.empty")}</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="onboarding-status-connected">
              {connected.map((s) => (
                <li
                  key={`${s.kind}-${s.id}`}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{s.name}</p>
                    {s.detail ? (
                      <p className="truncate text-xs text-muted-foreground">{s.detail}</p>
                    ) : null}
                  </div>
                  <StatusPill tone={s.status === "connected" ? "success" : "info"}>
                    {t(`status.${s.status}`)}
                  </StatusPill>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" onClick={onAddMore} data-testid="onboarding-status-add-more">
        {t("statusMode.addMore")}
      </Button>
    </div>
  );
}
